import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { DEFAULT_QUERY_RETRY, DEFAULT_STALE_TIME_MS } from './constants';

/**
 * HTTP statuses that a retry can never fix. A 401/403 is a statement about the
 * SESSION, not a transient transport failure: the http client has already tried
 * its single silent token refresh by the time the error surfaces here, so every
 * further attempt sends the same dead token and fails identically.
 *
 * Retrying them is not merely wasted work — it is user-visible. `handleUnauthorized()`
 * only runs once the query SETTLES, so with the default two retries an expired
 * session spends three round-trips in the error state before the redirect to
 * `/login` fires. That is exactly how the Menu screen came to show
 * "Не удалось загрузить меню" for what was really a signed-out session
 * (`menuApi.get`'s `Promise.all` rejects on the 401 from
 * `product-categories/by-merchant`, while its sibling products call answers fine).
 *
 * 403 is included for the same reason: a permission the token lacks now is not
 * one it will acquire on the next attempt.
 */
const NON_RETRYABLE_STATUSES = new Set([401, 403]);

/**
 * Read an HTTP status off an unknown error without importing `@noor/api` —
 * `@noor/config` sits BELOW it (the http client imports `env` from here), so a
 * real import would be a dependency cycle. `ApiError` carries a numeric
 * `status`; structural detection keeps this package dependency-free.
 */
function errorStatus(error: unknown): number | undefined {
  if (error === null || typeof error !== 'object') return undefined;
  const status: unknown = (error as { status?: unknown }).status;
  return typeof status === 'number' ? status : undefined;
}

/**
 * Retry transient failures, but never an auth failure — see
 * {@link NON_RETRYABLE_STATUSES}.
 */
function shouldRetry(failureCount: number, error: unknown): boolean {
  const status = errorStatus(error);
  if (status !== undefined && NON_RETRYABLE_STATUSES.has(status)) return false;
  return failureCount < DEFAULT_QUERY_RETRY;
}

export interface CreateQueryClientOptions {
  /**
   * Global mutation error handler. Called for every mutation that does NOT set
   * `meta.suppressErrorToast = true`. Wire in `toast.error` + `getApiErrorMessage`
   * at the app level so this package stays free of UI/API dependencies.
   */
  onMutationError?: (error: unknown) => void;
  /** Errors that must be reported even when a mutation owns its own toast. */
  onSuppressedMutationError?: (error: unknown) => void;
  /** Called once when a query has exhausted its retries. */
  onQueryError?: (error: unknown) => void;
}

/**
 * Factory so tests can spin up an isolated client (no retries, no cache leak).
 * The app uses a single long-lived instance created in main.tsx.
 */
export function createQueryClient(options: CreateQueryClientOptions = {}): QueryClient {
  const mutationCache =
    options.onMutationError || options.onSuppressedMutationError
      ? new MutationCache({
          onError(error, _variables, _context, mutation) {
            if (mutation.options.meta?.suppressErrorToast) {
              options.onSuppressedMutationError?.(error);
              return;
            }
            options.onMutationError?.(error);
          },
        })
      : undefined;
  const queryCache = options.onQueryError
    ? new QueryCache({ onError: (error) => options.onQueryError!(error) })
    : undefined;

  return new QueryClient({
    ...(mutationCache ? { mutationCache } : {}),
    ...(queryCache ? { queryCache } : {}),
    defaultOptions: {
      queries: {
        staleTime: DEFAULT_STALE_TIME_MS,
        retry: shouldRetry,
        refetchOnWindowFocus: false,
      },
      mutations: {
        retry: 0,
      },
    },
  });
}
